import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useTeamRole } from "@/hooks/useTeamRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { filterNameInput, filterPhoneInput, isValidPhone } from "@/lib/validation";

// Mirrors the limits enforced during onboarding (src/components/OnboardingWizard.tsx
// MAX_LENGTHS) and the server-side CHECK constraints on public.profiles.
const MAX_NAME_LENGTH = 100;
const MAX_PHONE_LENGTH = 20;

export default function MyProfile() {
  const { user, signOut } = useAuth();
  const { profile, updateProfile } = useProfile();
  const { teamRole } = useTeamRole();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);

  useEffect(() => {
    if (profile) {
      setName(profile.owner_name ?? "");
      setPhone(profile.owner_phone ?? "");
    }
  }, [profile]);

  const initials = (name || "U").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  function validate(): boolean {
    const errs: { name?: string; phone?: string } = {};
    if (!name.trim()) errs.name = "Full Name is required.";
    else if (name.length > MAX_NAME_LENGTH) errs.name = `Full Name must be ${MAX_NAME_LENGTH} characters or fewer.`;
    if (phone.length > MAX_PHONE_LENGTH) errs.phone = `Phone must be ${MAX_PHONE_LENGTH} characters or fewer.`;
    else if (phone && !isValidPhone(phone)) errs.phone = "Enter a valid phone number.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function save() {
    if (!validate()) return;
    setSaving(true);
    try {
      await updateProfile({ owner_name: name, owner_phone: phone });
      toast.success("Profile updated");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  const roleBadgeColor =
    teamRole === "owner" ? "bg-gold text-navy" :
    teamRole === "manager" ? "bg-primary text-primary-foreground" :
    "bg-muted-foreground text-card";

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-card rounded-2xl shadow-sm border p-8">
        <h1 className="text-xl font-bold text-navy text-center mb-6">My Profile</h1>

        <div className="flex flex-col items-center mb-6">
          <div className="h-16 w-16 rounded-full bg-navy flex items-center justify-center text-gold text-xl font-bold mb-2">
            {initials}
          </div>
          {teamRole && (
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${roleBadgeColor}`}>
              {teamRole}
            </span>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={e => setName(filterNameInput(e.target.value))}
              required
              maxLength={MAX_NAME_LENGTH}
              className="mt-1"
            />
            {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={user?.email ?? ""} readOnly className="mt-1 bg-muted" />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              type="text"
              inputMode="tel"
              value={phone}
              onChange={e => setPhone(filterPhoneInput(e.target.value))}
              maxLength={MAX_PHONE_LENGTH}
              className="mt-1"
            />
            {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
          </div>
        </div>

        <Button onClick={save} disabled={saving || !name.trim()} className="w-full mt-6 bg-gold text-gold-foreground hover:bg-gold/90 font-bold min-h-[44px]">
          {saving ? "Saving..." : "Save Changes"}
        </Button>

        <button
          onClick={() => setSignOutConfirmOpen(true)}
          className="w-full mt-4 text-center text-sm text-hot hover:underline flex items-center justify-center gap-1.5"
        >
          <LogOut className="h-4 w-4" /> Sign Out
        </button>
      </div>

      <AlertDialog open={signOutConfirmOpen} onOpenChange={setSignOutConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to log out?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll need to sign in again to access your dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => signOut()}>Log Out</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
